import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  Sparkles,
  XCircle,
} from "lucide-react";
import { EnumBadge } from "@/components/badges";
import { StructuredOutputRegressionPanel } from "@/components/structured-output-regression-panel";
import { StructuredOutputBackgroundRegressionPanel } from "@/components/structured-output-background-regression-panel";
import { StructuredOutputDiffView, structuredOutputFieldChangeCount } from "@/components/structured-output-diff-view";
import { TranscriptView } from "@/components/transcript-view";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useStructuredOutputRerunApply,
  useStructuredOutputRerunPreview,
} from "@/hooks/use-structured-output-rerun";
import { useStructuredOutputBackgroundRegression } from "@/hooks/use-structured-output-background-regression";
import { failClassLabel, failClassTone } from "@/lib/intake-format";
import { downloadJsonFile } from "@/lib/download-json";
import { parseStructuredOutputRerunTargets } from "@/lib/structured-output-rerun-targets";
import { cn } from "@/utils";
import type {
  StructuredOutputRerunApplyResponse,
  StructuredOutputRerunBlockedRow,
  StructuredOutputRerunPreviewResponse,
  StructuredOutputRerunReadyRow,
} from "@/types/structured-output-rerun";
import type {
  StructuredOutputRegressionCaseType,
  StructuredOutputRegressionOverrides,
} from "@/types/structured-output-regression";

type Step = "input" | "preview" | "applied";

function outputSummary(outputs: Record<string, unknown>, caseType: string): string {
  if (caseType === "bv") {
    const success = outputs.success;
    const failReason = outputs.fail_reason;
    const parts = [
      success !== undefined ? `success=${String(success)}` : null,
      failReason ? `fail_reason=${String(failReason)}` : null,
    ].filter(Boolean);
    return parts.join(" · ") || "—";
  }
  const paStatus = outputs.pa_status;
  const failReason = outputs.fail_reason;
  const parts = [
    paStatus ? `pa_status=${String(paStatus)}` : null,
    failReason ? `fail_reason=${String(failReason)}` : null,
  ].filter(Boolean);
  return parts.join(" · ") || "—";
}

function buildRegressionExportPayload({
  preview,
  regressionCaseType,
  regressionOverrides,
  targets,
  applyResult,
}: {
  preview: StructuredOutputRerunPreviewResponse;
  regressionCaseType: StructuredOutputRegressionCaseType;
  regressionOverrides: StructuredOutputRegressionOverrides | null;
  targets: Array<{ case_id: string; call_id?: string }>;
  applyResult?: StructuredOutputRerunApplyResponse | null;
}) {
  return {
    exported_at: new Date().toISOString(),
    regression: {
      case_type: regressionCaseType,
      ...(regressionOverrides?.prompt_override
        ? { prompt_override: regressionOverrides.prompt_override }
        : {}),
      ...(regressionOverrides?.schema_override
        ? { schema_override: regressionOverrides.schema_override }
        : {}),
    },
    request: { targets },
    summary: preview.summary,
    ready: preview.ready.map((row) => ({
      ...row,
      field_change_count: structuredOutputFieldChangeCount(
        row.current_outputs,
        row.new_outputs,
      ),
      previous_summary: outputSummary(row.current_outputs, row.case_type),
      new_summary: outputSummary(row.new_outputs, row.case_type),
    })),
    blocked: preview.blocked,
    ...(applyResult ? { apply: applyResult } : {}),
  };
}

function regressionExportFilename(caseType: string): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `so-regression-${caseType}-${stamp}.json`;
}

export default function StructuredOutputRerunPage() {
  const [searchParams] = useSearchParams();
  const initialIdsParam = searchParams.get("case_ids") || "";

  const [rawIds, setRawIds] = useState<string>(
    initialIdsParam.replace(/,/g, "\n"),
  );
  const [step, setStep] = useState<Step>("input");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [regressionCaseType, setRegressionCaseType] =
    useState<StructuredOutputRegressionCaseType>("pa");
  const [regressionOverrides, setRegressionOverrides] =
    useState<StructuredOutputRegressionOverrides | null>(null);
  const [backgroundRegressionEnabled, setBackgroundRegressionEnabled] =
    useState(false);
  const [perStatusLimit, setPerStatusLimit] = useState(5);

  const {
    result: preview,
    loading: previewLoading,
    error: previewError,
    run: runPreview,
    reset: resetPreview,
  } = useStructuredOutputRerunPreview();
  const {
    result: applyResult,
    loading: applying,
    error: applyError,
    run: runApply,
    reset: resetApply,
  } = useStructuredOutputRerunApply();
  const {
    job: backgroundJob,
    starting: backgroundStarting,
    polling: backgroundPolling,
    error: backgroundError,
    start: startBackgroundRegression,
    reset: resetBackgroundRegression,
  } = useStructuredOutputBackgroundRegression();

  const targets = useMemo(() => parseStructuredOutputRerunTargets(rawIds), [rawIds]);
  const ready = preview?.ready ?? [];
  const blocked = preview?.blocked ?? [];

  const previewRequestBody = useMemo(() => {
    const body = {
      targets,
      case_type: regressionCaseType,
      ...(regressionOverrides?.prompt_override
        ? { prompt_override: regressionOverrides.prompt_override }
        : {}),
      ...(regressionOverrides?.schema_override
        ? { schema_override: regressionOverrides.schema_override }
        : {}),
    };
    return body;
  }, [targets, regressionCaseType, regressionOverrides]);

  const runPreviewBatch = useCallback(async () => {
    if (targets.length === 0 && !backgroundRegressionEnabled) return;

    if (backgroundRegressionEnabled) {
      void startBackgroundRegression({
        case_type: regressionCaseType,
        per_status_limit: perStatusLimit,
        ...(regressionOverrides?.prompt_override
          ? { prompt_override: regressionOverrides.prompt_override }
          : {}),
        ...(regressionOverrides?.schema_override
          ? { schema_override: regressionOverrides.schema_override }
          : {}),
      });
    }

    if (targets.length === 0) return;

    const data = await runPreview(previewRequestBody);
    setSelected(new Set(data.ready.map((r) => r.case_id)));
    setStep("preview");
    return data;
  }, [
    targets.length,
    backgroundRegressionEnabled,
    startBackgroundRegression,
    regressionCaseType,
    perStatusLimit,
    regressionOverrides,
    previewRequestBody,
    runPreview,
  ]);

  useEffect(() => {
    if (initialIdsParam && step === "input") {
      const ids = initialIdsParam
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (ids.length > 0) {
        void runPreviewBatch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreview = useCallback(async () => {
    await runPreviewBatch();
  }, [runPreviewBatch]);

  const handleApply = useCallback(async () => {
    if (!preview) return;
    const items = preview.ready
      .filter((r) => selected.has(r.case_id))
      .map((r) => ({
        case_id: r.case_id,
        call_id: r.call_id,
        new_outputs: r.new_outputs,
      }));
    if (items.length === 0) return;
    const ok = window.confirm(
      `Update structured output for ${items.length} selected case(s)? This writes the previewed output and updates case status. Successful cases may move to successfulCalls; failed cases will not trigger automatic retries.`,
    );
    if (!ok) return;
    await runApply({ items });
    setStep("applied");
  }, [preview, selected, runApply]);

  const handleStartOver = useCallback(() => {
    resetPreview();
    resetApply();
    resetBackgroundRegression();
    setSelected(new Set());
    setRawIds("");
    setStep("input");
  }, [resetPreview, resetApply, resetBackgroundRegression]);

  const handleExportResults = useCallback(() => {
    if (!preview) return;
    downloadJsonFile(
      regressionExportFilename(regressionCaseType),
      buildRegressionExportPayload({
        preview,
        regressionCaseType,
        regressionOverrides,
        targets,
        applyResult: step === "applied" ? applyResult : null,
      }),
    );
  }, [
    preview,
    regressionCaseType,
    regressionOverrides,
    targets,
    applyResult,
    step,
  ]);

  const toggleSelected = useCallback((caseId: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(caseId);
      else next.delete(caseId);
      return next;
    });
  }, []);

  const toggleAllReady = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelected(new Set(ready.map((r) => r.case_id)));
      } else {
        setSelected(new Set());
      }
    },
    [ready],
  );

  const selectedCount = selected.size;
  const changedCount = ready.filter((r) =>
    selected.has(r.case_id) && structuredOutputFieldChangeCount(r.current_outputs, r.new_outputs) > 0,
  ).length;

  const hasPreviewResults = Boolean(preview);

  const goToInputStep = useCallback(() => {
    if (step === "input") return;
    setStep("input");
  }, [step]);

  const goToPreviewStep = useCallback(() => {
    if (!hasPreviewResults || step === "preview") return;
    setStep("preview");
  }, [hasPreviewResults, step]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Sparkles className="h-6 w-6" />
            Structured Output Rerun
          </h1>
          <p className="text-sm text-muted-foreground">
            Re-run Gemini on stored call transcripts for specific case IDs. Tune
            the PA or BV prompt and schema for regression testing, preview new
            structured output, then apply the exact previewed results for the
            cases you select.
          </p>
        </div>
      </div>

      <ol className="flex items-center gap-2 text-xs">
        <StepPill
          index={1}
          label="Case IDs"
          active={step === "input"}
          done={step !== "input"}
          clickable={step !== "input"}
          onClick={goToInputStep}
        />
        <StepPill
          index={2}
          label="Preview"
          active={step === "preview"}
          done={step === "applied"}
          clickable={hasPreviewResults && step !== "preview"}
          onClick={goToPreviewStep}
        />
        <StepPill index={3} label="Apply" active={step === "applied"} />
      </ol>

      <StructuredOutputRegressionPanel
        caseType={regressionCaseType}
        onCaseTypeChange={setRegressionCaseType}
        onOverridesChange={setRegressionOverrides}
      />

      <StructuredOutputBackgroundRegressionPanel
        job={backgroundJob}
        polling={backgroundPolling}
        starting={backgroundStarting}
        error={backgroundError}
        onDismiss={resetBackgroundRegression}
      />

      {step === "input" ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="so-rerun-case-ids">
              Case IDs (one per line; optional call ID after comma)
            </label>
            <textarea
              id="so-rerun-case-ids"
              value={rawIds}
              onChange={(e) => setRawIds(e.target.value)}
              rows={8}
              className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
              placeholder={"018ac3b8-ec5f-4380-b502-71269a1bb108\n05b57896-6a6a-40bd-9fb0-baa84c6243f1,abc-call-id-123"}
            />
            <p className="text-xs text-muted-foreground">
              {targets.length} target{targets.length === 1 ? "" : "s"} parsed.
              Use one case ID per line. Add{" "}
              <code className="rounded bg-muted px-1 py-0.5">,call_id</code> on
              the same line to rerun a specific call instead of the latest.
              Each case must have a stored transcript and structured_outputs_history
              for the selected call. Cases must match the selected{" "}
              {regressionCaseType.toUpperCase()} regression mode.
            </p>
          </div>

          <div className="rounded-md border bg-muted/20 p-4 space-y-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={backgroundRegressionEnabled}
                onChange={(e) => setBackgroundRegressionEnabled(e.target.checked)}
              />
              <span>
                <span className="font-medium">Run background regression by status</span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  In parallel with preview, sample recent calls across each{" "}
                  {regressionCaseType === "bv"
                    ? "BV success category and fail reason"
                    : "PA status and fail reason"}{" "}
                  bucket using the prompt/schema above, then compare key fields.
                </span>
              </span>
            </label>
            {backgroundRegressionEnabled ? (
              <div className="flex flex-wrap items-center gap-3 pl-6">
                <label className="flex items-center gap-2 text-sm" htmlFor="so-per-status-limit">
                  Calls per status
                  <input
                    id="so-per-status-limit"
                    type="number"
                    min={1}
                    max={20}
                    value={perStatusLimit}
                    onChange={(e) => {
                      const next = Number.parseInt(e.target.value, 10);
                      if (Number.isNaN(next)) return;
                      setPerStatusLimit(Math.min(20, Math.max(1, next)));
                    }}
                    className="w-20 rounded-md border border-input bg-background px-2 py-1 font-mono text-sm"
                  />
                </label>
                <p className="text-xs text-muted-foreground">
                  Scans recent cases (up to 3,000) and pulls up to this many latest-call
                  samples per status bucket. Results poll in the panel above.
                </p>
              </div>
            ) : null}
          </div>

          {previewError ? (
            <p className="text-sm text-destructive">{previewError}</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => void handlePreview()}
              disabled={
                (targets.length === 0 && !backgroundRegressionEnabled) ||
                previewLoading ||
                backgroundStarting
              }
            >
              {previewLoading || backgroundStarting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {previewLoading ? "Running preview…" : "Starting background job…"}
                </>
              ) : (
                backgroundRegressionEnabled && targets.length === 0
                  ? "Run background regression"
                  : backgroundRegressionEnabled
                    ? "Generate preview + background regression"
                    : "Generate preview"
              )}
            </Button>
          </div>
        </div>
      ) : null}

      {step === "preview" ? (
        <div className="space-y-6">
          {previewLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating preview for {targets.length} targets…
            </div>
          ) : null}
          {previewError ? (
            <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {previewError}
            </div>
          ) : null}
          {preview ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-md border border-green-200 bg-green-50 px-3 py-1.5 dark:border-green-900 dark:bg-green-950/40">
                    Preview ready · {ready.length}
                  </span>
                  <span className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-1.5">
                    Blocked · {blocked.length}
                  </span>
                  <span className="rounded-md border bg-muted/30 px-3 py-1.5">
                    Total · {preview.summary.total}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleExportResults}
                  disabled={previewLoading}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  Export JSON
                </Button>
              </div>

              {ready.length > 0 ? (
                <section className="space-y-3">
                  <header className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-sm font-semibold text-green-700 dark:text-green-300">
                        Preview ({ready.length})
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        Select cases to persist the previewed structured output. Apply
                        writes exactly what you saw in preview and updates case status
                        from the success flag.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={selectedCount === ready.length && ready.length > 0}
                        onChange={(e) => toggleAllReady(e.target.checked)}
                      />
                      Select all
                    </label>
                  </header>
                  <div className="space-y-4">
                    {ready.map((row) => (
                      <PreviewCard
                        key={row.case_id}
                        row={row}
                        selected={selected.has(row.case_id)}
                        onSelectedChange={(checked) =>
                          toggleSelected(row.case_id, checked)
                        }
                      />
                    ))}
                  </div>
                </section>
              ) : null}

              {blocked.length > 0 ? (
                <section className="space-y-2">
                  <h2 className="text-sm font-semibold text-destructive">
                    Blocked ({blocked.length})
                  </h2>
                  <ScrollArea className="max-h-64 w-full rounded-md border">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-muted/80">
                        <tr className="border-b text-left">
                          <th className="px-3 py-2 font-medium">Case ID</th>
                          <th className="px-3 py-2 font-medium">Reason</th>
                          <th className="px-3 py-2 font-medium">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blocked.map((row) => (
                          <BlockedRow key={row.case_id} row={row} />
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </section>
              ) : null}

              {applyError ? (
                <p className="text-sm text-destructive">{applyError}</p>
              ) : null}

              <div className="flex flex-wrap justify-between gap-2">
                <Button variant="outline" onClick={handleStartOver}>
                  Start over
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => void handlePreview()}
                    disabled={previewLoading || applying}
                  >
                    Re-preview
                  </Button>
                  <Button
                    onClick={() => void handleApply()}
                    disabled={
                      applying || selectedCount === 0 || previewLoading
                    }
                  >
                    {applying ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Applying…
                      </>
                    ) : (
                      `Apply ${selectedCount} case${selectedCount === 1 ? "" : "s"}`
                    )}
                  </Button>
                </div>
              </div>
              {selectedCount > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {changedCount} of {selectedCount} selected case
                  {selectedCount === 1 ? "" : "s"} show output changes vs current.
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {step === "applied" && applyResult ? (
        <div className="space-y-4">
          <div className="rounded-md border border-green-600/30 bg-green-600/10 p-4">
            <div className="flex items-center gap-2 text-green-900 dark:text-green-100">
              <CheckCircle2 className="h-5 w-5" />
              <p className="font-medium">Apply complete</p>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <ResultStat label="Processed" value={applyResult.processed} />
              <ResultStat
                label="Completed"
                value={applyResult.summary.completed}
              />
              <ResultStat label="Skipped" value={applyResult.summary.skipped} />
              <ResultStat label="Errors" value={applyResult.summary.errors} />
            </dl>
          </div>

          {applyResult.results.some((r) => r.result === "error" || r.success === false) ? (
            <ScrollArea className="max-h-48 w-full rounded-md border">
              <ul className="px-3 py-2 text-xs">
                {applyResult.results
                  .filter((r) => r.result === "error" || r.success === false)
                  .map((r, i) => (
                    <li key={i} className="flex items-start gap-2 border-b py-1 last:border-b-0">
                      <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                      <span>
                        <span className="font-mono text-[11px]">
                          {r.case_id || r.document_id || "(unknown)"}
                        </span>
                        : {r.detail || r.reason || "error"}
                      </span>
                    </li>
                  ))}
              </ul>
            </ScrollArea>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleStartOver}>
              Run another batch
            </Button>
            {preview ? (
              <Button type="button" variant="outline" onClick={handleExportResults}>
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Export JSON
              </Button>
            ) : null}
            <Button asChild variant="ghost">
              <Link to="/cases">View cases</Link>
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PreviewCard({
  row,
  selected,
  onSelectedChange,
}: {
  row: StructuredOutputRerunReadyRow;
  selected: boolean;
  onSelectedChange: (checked: boolean) => void;
}) {
  const fieldChangeCount = structuredOutputFieldChangeCount(
    row.current_outputs,
    row.new_outputs,
  );
  const changed = fieldChangeCount > 0;
  const hasTranscript = row.transcript.length > 0;

  return (
    <article
      className={cn(
        "rounded-lg border p-4",
        selected ? "border-primary/40 bg-primary/5" : "bg-card",
      )}
    >
      <div className="flex flex-wrap items-start gap-3">
        <input
          type="checkbox"
          className="mt-1"
          checked={selected}
          onChange={(e) => onSelectedChange(e.target.checked)}
          aria-label={`Select ${row.case_id}`}
        />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <code className="text-xs">{row.case_id}</code>
            {!changed ? (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                No change
              </span>
            ) : (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-900 dark:bg-amber-950/50 dark:text-amber-100">
                {fieldChangeCount} field{fieldChangeCount === 1 ? "" : "s"} changed
              </span>
            )}
          </div>
          <p className="text-sm font-medium">{row.name}</p>
          <p className="text-xs text-muted-foreground">
            {row.product_name || "—"} · {row.payer_name || "—"} ·{" "}
            {row.case_type.toUpperCase()} · attempt {row.attempts}
            {row.fail_class ? (
              <>
                {" "}
                ·{" "}
                <EnumBadge
                  value={failClassLabel(row.fail_class)}
                  semanticTone={failClassTone(row.fail_class)}
                />
              </>
            ) : null}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-muted-foreground">
            <span>
              call_id={row.call_id}
              {row.phone_number ? ` · ${row.phone_number}` : ""}
              {row.transcript_timestamp ? ` · ${row.transcript_timestamp}` : ""}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 pt-1 text-[11px]">
            <span className="rounded border border-red-200/70 bg-red-50/60 px-2 py-0.5 text-red-900 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-100">
              Previous: {outputSummary(row.current_outputs, row.case_type)}
            </span>
            <span className="rounded border border-green-200/70 bg-green-50/60 px-2 py-0.5 text-green-900 dark:border-green-900/50 dark:bg-green-950/30 dark:text-green-100">
              New: {outputSummary(row.new_outputs, row.case_type)}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <div className="space-y-2 rounded-md border bg-muted/20 p-3">
          <p className="text-xs font-medium text-muted-foreground">
            Call transcript
            {hasTranscript
              ? ` (${row.transcript.length} message${row.transcript.length === 1 ? "" : "s"})`
              : ""}
          </p>
          <ScrollArea className="max-h-[32rem] rounded-md border bg-background p-3">
            {hasTranscript ? (
              <TranscriptView messages={row.transcript} />
            ) : (
              <p className="text-sm italic text-muted-foreground">
                No transcript messages for this call
              </p>
            )}
          </ScrollArea>
        </div>
        <div className="space-y-2 rounded-md border p-3">
          <p className="text-xs font-medium">Structured output comparison</p>
          <StructuredOutputDiffView
            currentOutputs={row.current_outputs}
            newOutputs={row.new_outputs}
          />
        </div>
      </div>
    </article>
  );
}

function BlockedRow({ row }: { row: StructuredOutputRerunBlockedRow }) {
  return (
    <tr className="border-b last:border-b-0">
      <td className="px-3 py-2 font-mono text-[11px]">{row.case_id}</td>
      <td className="px-3 py-2">{row.reason}</td>
      <td className="px-3 py-2 text-muted-foreground">{row.detail || "—"}</td>
    </tr>
  );
}

function StepPill({
  index,
  label,
  active,
  done,
  clickable,
  onClick,
}: {
  index: number;
  label: string;
  active?: boolean;
  done?: boolean;
  clickable?: boolean;
  onClick?: () => void;
}) {
  const className = cn(
    "flex items-center gap-2 rounded-md border px-3 py-1.5 text-left transition-colors",
    active
      ? "border-primary bg-primary text-primary-foreground"
      : done
        ? "border-green-200 bg-green-50 text-green-900 dark:border-green-900 dark:bg-green-950/40 dark:text-green-100"
        : "border-border bg-background text-muted-foreground",
    clickable &&
      !active &&
      "cursor-pointer hover:border-primary/40 hover:bg-primary/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  );

  if (clickable && onClick) {
    return (
      <li>
        <button type="button" className={className} onClick={onClick}>
          <span className="font-mono">{index}</span>
          <span className="font-medium">{label}</span>
        </button>
      </li>
    );
  }

  return (
    <li className={className}>
      <span className="font-mono">{index}</span>
      <span className="font-medium">{label}</span>
    </li>
  );
}

function ResultStat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
